import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { backendProvider } from '../../services/backend';
import { preferencesService } from '../../services/preferences.service';
import { n8nService } from '../core/services/n8n.service';
import { brainiacsService } from '../core/services/brainiacs.service';
import { queryKeys } from '../../services/query/queryKeys';

/**
 * Ensure a Nexus instance URL is absolute. The nexus_setup workflow stores the
 * host only (e.g. "abc.brainiacs.nexus"); without a scheme the browser resolves
 * `form.action` relative to the current origin (os.brainiacs.wtf/abc.brainiacs.nexus…).
 */
const toAbsoluteNexusUrl = (raw: string): string => {
  const trimmed = raw.trim().replace(/\/+$/, '');
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Nexus — "The Advanced Personal Assistant AI" (Alpha).
 *
 * Per-user state is driven by the Appwrite `nexus` user label:
 * - No `nexus` label  → user has no provisioned server → show "Sign up for Nexus".
 * - Has `nexus` label → server provisioned → show "Open Nexus" (Appwrite-JWT SSO handoff).
 *
 * Under superadmin impersonation the label AND the instance URL are resolved from
 * the impersonated user (via list_all_users) — never from the admin's own session,
 * since account.getPrefs()/currentUser.labels always reflect the signed-in admin.
 *
 * The tab is available to every workspace user so the Modules catalog can route
 * directly to its onboarding/payment experience.
 */
const NexusTab: React.FC = () => {
  const { currentUser, targetEmail } = useAuth();
  const isImpersonating = Boolean(targetEmail && targetEmail !== currentUser?.email);

  // Resolve the impersonated user's identity (labels + nexus_url) via the same admin
  // action CampaignIndex uses. React Query dedupes with that query by shared key.
  const impersonatedUserQuery = useQuery({
    queryKey: queryKeys.users.byEmail(isImpersonating ? targetEmail : null),
    queryFn: async () => {
      const adminEmail = currentUser?.email || 'brainiac@brainiacsbots.com';
      const response = await brainiacsService.customAction('list_all_users', {
        admin_email: adminEmail,
      });
      if (!response.success || !Array.isArray(response.data)) return null;
      const email = (targetEmail || '').toLowerCase().trim();
      return response.data.find(
        (u: any) => String(u.email || '').toLowerCase().trim() === email
      ) ?? null;
    },
    enabled: isImpersonating,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const [nexusUrl, setNexusUrl] = useState<string | null>(null);
  const [signingUp, setSigningUp] = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the effective user's private instance URL (written by the nexus_setup workflow).
  // This is loaded unconditionally: the presence of a nexus_url is what tells us a server
  // was provisioned — the `nexus` label isn't reliably set by the workflow.
  useEffect(() => {
    let active = true;

    // Impersonation: the instance URL is surfaced by list_all_users (prefs.nexus_url).
    // account.getPrefs() would return the admin's own prefs, so it's never used here.
    if (isImpersonating) {
      setNexusUrl(impersonatedUserQuery.data?.nexus_url || null);
      return;
    }

    preferencesService
      .getPreferences()
      .then((prefs) => {
        if (active) setNexusUrl(prefs.nexus_url || null);
      })
      .catch(() => {
        if (active) setNexusUrl(null);
      });
    return () => {
      active = false;
    };
  }, [isImpersonating, impersonatedUserQuery.data]);

  // A provisioned Nexus server is signalled by a stored nexus_url; the `nexus` label is
  // kept as a secondary signal so a labelled-but-URL-pending user still sees the
  // "Open Nexus" state (with an "instance isn't ready yet" hint) rather than "Sign up".
  const hasNexusLabel = isImpersonating
    ? (impersonatedUserQuery.data?.labels?.includes('nexus') ?? false)
    : (currentUser?.labels?.includes('nexus') ?? false);
  const hasNexus = Boolean(nexusUrl) || hasNexusLabel;

  const handleSignup = async () => {
    setSigningUp(true);
    setError(null);
    try {
      // Identity is resolved server-side by n8n-proxy — no client-sent email.
      await n8nService.setupNexus();
      setSignupDone(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to start Nexus setup. Please try again.');
    } finally {
      setSigningUp(false);
    }
  };

  // Mint a fresh short-lived Appwrite JWT and POST it to the user's Nexus instance.
  // Top-level form submit keeps the token in the request body (never the URL).
  const openNexus = async (instanceUrl: string) => {
    setOpening(true);
    setError(null);
    try {
      const jwt = await backendProvider.auth.getIdToken(true);
      if (!jwt) throw new Error('Could not mint an Appwrite session token.');

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${toAbsoluteNexusUrl(instanceUrl)}/auth/appwrite`;
      form.target = '_blank'; // open the instance in a new tab — OS stays as the login portal

      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'jwt';
      field.value = jwt;
      form.appendChild(field);

      document.body.appendChild(form);
      form.submit();
      form.remove();
    } catch (err: any) {
      setError(err?.message || 'Failed to open Nexus. Please try again.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="mb-8 space-y-6">
      <div className="campaign-hero-aurora relative overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(145deg,#101010,#080808)] px-5 py-5 shadow-[0_18px_60px_rgba(0,0,0,0.32)] md:px-7 md:py-7">
        <div className="campaign-hero-aurora__band campaign-hero-aurora__band--primary" />
        <div className="campaign-hero-aurora__band campaign-hero-aurora__band--secondary" />
        <div className="campaign-hero-aurora__band campaign-hero-aurora__band--accent" />
        <div className="campaign-hero-aurora__veil" />
        <div className="absolute -right-10 bottom-0 hidden h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,rgba(21,251,255,0.10),transparent_60%)] blur-3xl lg:block" />

        <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 max-w-[34rem] self-center md:self-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/18 bg-cyan-400/8 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/90">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-300" />
              Alpha
            </div>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">Nexus</h2>
            <p className="mt-4 max-w-2xl text-base leading-7 text-gray-300 md:text-lg">
              The Advanced Personal Assistant AI.
            </p>

            {!hasNexus ? (
              <>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400">
                  Sign up now. Personal AI Server costs €49/month + LLM usage.
                </p>
                <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                  <button
                    onClick={handleSignup}
                    disabled={signingUp || signupDone || isImpersonating}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-[13px] font-black uppercase tracking-[0.14em] text-black transition-all hover:scale-[1.01] hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Icon
                      icon={signupDone ? 'solar:check-circle-bold' : 'solar:bolt-bold'}
                      className="h-4 w-4"
                    />
                    {signingUp ? 'Setting up…' : signupDone ? 'Setup requested' : 'Sign up for Nexus'}
                  </button>
                  {isImpersonating && (
                    <span className="text-sm leading-6 text-gray-400">
                      Can't provision Nexus while impersonating — sign up runs against your own account.
                    </span>
                  )}
                </div>
                {signupDone && (
                  <p className="mt-3 text-sm leading-6 text-cyan-200/80">
                    We're provisioning your personal AI server. You'll get access here once it's ready.
                  </p>
                )}
              </>
            ) : (
              <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <button
                  onClick={() => nexusUrl && openNexus(nexusUrl)}
                  disabled={opening || !nexusUrl}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-[13px] font-black uppercase tracking-[0.14em] text-black transition-all hover:scale-[1.01] hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Icon icon="solar:arrow-right-up-linear" className="h-4 w-4" />
                  {opening ? 'Opening…' : 'Open Nexus'}
                </button>
                {!nexusUrl && (
                  <span className="text-sm leading-6 text-gray-400">
                    Your instance isn't ready yet — check back shortly.
                  </span>
                )}
              </div>
            )}

            {error && <p className="mt-3 text-sm leading-6 text-red-300/90">{error}</p>}
          </div>

          <div className="relative hidden shrink-0 self-center md:flex md:items-center md:justify-end">
            <div className="absolute inset-x-6 bottom-2 top-6 rounded-full bg-[radial-gradient(circle_at_center,rgba(21,251,255,0.16),transparent_62%)] blur-2xl" />
            <div className="relative flex h-40 w-40 items-center justify-center rounded-[32px] border border-cyan-300/20 bg-cyan-400/8">
              <Icon icon="solar:server-square-cloud-bold-duotone" className="h-20 w-20 text-cyan-200/70" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NexusTab;
